import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { WmsStockInfo } from '../types';
import StockUpdatedAt from './StockUpdatedAt';
import WarehouseStock from './WarehouseStock';

export default function ProductStockSummary({ stock, unit = 'pz' }: { stock?: WmsStockInfo | null; unit?: string }) {
  const tooltipId = useId();
  const [anchor, setAnchor] = useState<DOMRect | null>(null);

  if (!stock?.wms_available) return <span className="text-xs text-gray-500">N/D</span>;

  const quantity = stock.available_quantity ?? 0;
  const color = quantity <= 0 ? 'bg-red-100 text-red-800'
    : quantity < 5 ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800';
  const formatQuantity = (value: number) => `${value.toLocaleString('it-IT')} ${unit}`;

  return (
    <div
      tabIndex={0}
      aria-label={`Disponibilità: ${formatQuantity(quantity)}`}
      aria-describedby={anchor ? tooltipId : undefined}
      className="inline-flex cursor-help flex-col items-center gap-1 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      onMouseEnter={(event) => setAnchor(event.currentTarget.getBoundingClientRect())}
      onMouseLeave={() => setAnchor(null)}
      onFocus={(event) => setAnchor(event.currentTarget.getBoundingClientRect())}
      onBlur={() => setAnchor(null)}
      onKeyDown={(event) => { if (event.key === 'Escape') { event.stopPropagation(); setAnchor(null); } }}
    >
      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>
        {formatQuantity(quantity)}
      </span>
      <StockUpdatedAt stock={stock} />
      {anchor && createPortal(
        <div
          id={tooltipId}
          role="tooltip"
          className="pointer-events-none fixed z-50 w-72 max-w-[calc(100vw-16px)] whitespace-normal rounded-lg border border-gray-200 bg-white p-3 text-left text-xs text-gray-700 shadow-lg"
          style={{
            left: Math.max(8, Math.min(anchor.left + anchor.width / 2 - 144, window.innerWidth - 296)),
            ...(anchor.top > window.innerHeight / 2
              ? { bottom: window.innerHeight - anchor.top + 8 }
              : { top: anchor.bottom + 8 }),
          }}
        >
          <p className="mb-2 font-semibold text-gray-900">Dettaglio disponibilità</p>
          <p>Disponibile: <strong>{formatQuantity(quantity)}</strong></p>
          {stock.reserved_quantity != null && <p>Riservata: <strong>{formatQuantity(stock.reserved_quantity)}</strong></p>}
          {stock.total_quantity != null && <p>Totale: <strong>{formatQuantity(stock.total_quantity)}</strong></p>}
          {stock.warehouses?.length ? (
            <div className="mt-2 border-t border-gray-100 pt-2"><WarehouseStock stock={stock} unit={unit} /></div>
          ) : stock.warehouse_name || stock.warehouse_id ? (
            <p className="mt-2">Magazzino: {stock.warehouse_name || stock.warehouse_id}</p>
          ) : null}
        </div>,
        document.body,
      )}
    </div>
  );
}
